import { createTheme } from "@mui/material";

/**
 * Creates a unified theme with system preference support using colorSchemes API
 *
 * Features:
 * - Professional color palette suitable for a design and optimization tool.
 * - Colors inspired by workshop materials like wood, metal, and blueprints.
 * - Clear visual hierarchy to enhance usability and focus on the task.
 * - Automatic system preference detection for light and dark modes.
 * - Manual mode switching capability.
 * - CSS variables for better performance.
 * - Prevents SSR flickering on server-rendered pages.
 */
export const theme = createTheme({
    cssVariables: {
        // Use class-based selector for manual mode switching
        colorSchemeSelector: "class",
    },
    // Define both light and dark color schemes inspired by workshop materials
    colorSchemes: {
        light: {
            palette: {
                primary: {
                    main: "#475569", // Slate Gray - Professional and serious, like metal tools
                    light: "#64748B", // Lighter slate
                    dark: "#334155", // Deeper slate
                    contrastText: "#FFFFFF",
                },
                secondary: {
                    main: "#A16207", // Golden Brown - Evokes wood and craftsmanship
                    light: "#CA8A04", // Lighter brown
                    dark: "#854D0E", // Deeper brown
                    contrastText: "#FFFFFF",
                },
                success: {
                    main: "#059669", // Emerald - For successful optimization
                    light: "#10B981",
                    dark: "#047857",
                    contrastText: "#FFFFFF",
                },
                warning: {
                    main: "#F59E0B", // Amber - For warnings like high waste
                    light: "#FBBF24",
                    dark: "#D97706",
                    contrastText: "#000000",
                },
                error: {
                    main: "#DC2626", // Red - For errors or invalid configurations
                    light: "#EF4444",
                    dark: "#B91C1C",
                    contrastText: "#FFFFFF",
                },
                info: {
                    main: "#0284C7", // Sky Blue - Like blueprints, for informational elements
                    light: "#0EA5E9",
                    dark: "#0369A1",
                    contrastText: "#FFFFFF",
                },
                text: {
                    primary: "#1E293B", // Dark Slate for high readability
                    secondary: "rgba(30, 41, 59, 0.7)",
                    disabled: "rgba(0, 0, 0, 0.38)", // Standard MUI disabled text for light theme
                },
                background: {
                    default: "#F1F5F9", // Light Slate Gray, like a clean workshop floor
                    paper: "#FFFFFF", // White for canvases and cards
                },
            },
        },
        dark: {
            palette: {
                primary: {
                    main: "#64748B", // Darker slate for better contrast in dark mode
                    light: "#94A3B8",
                    dark: "#475569",
                    contrastText: "#FFFFFF", // White text for better contrast
                },
                secondary: {
                    main: "#FBBF24", // Brighter Amber/Gold for accents
                    light: "#FCD34D",
                    dark: "#F59E0B",
                    contrastText: "#0F172A",
                },
                success: {
                    main: "#34D399", // Bright green for success
                    light: "#6EE7B7",
                    dark: "#10B981",
                    contrastText: "#0F172A",
                },
                warning: {
                    main: "#FEF08A", // Bright yellow for warnings
                    light: "#FEF08A",
                    dark: "#FBBF24",
                    contrastText: "#0F172A",
                },
                error: {
                    main: "#F87171", // Bright red for errors
                    light: "#FCA5A5",
                    dark: "#EF4444",
                    contrastText: "#0F172A",
                },
                info: {
                    main: "#38BDF8", // Bright sky blue for info
                    light: "#7DD3FC",
                    dark: "#0EA5E9",
                    contrastText: "#0F172A",
                },
                text: {
                    primary: "#FFFFFF", // Pure white for maximum contrast in dark mode
                    secondary: "rgba(255, 255, 255, 0.85)", // High contrast secondary text
                    disabled: "rgba(255, 255, 255, 0.4)",
                },
                background: {
                    default: "#0F172A", // Very dark blue-slate, like a night workshop
                    paper: "#1E293B", // Dark slate for surfaces
                },
                divider: "rgba(241, 245, 249, 0.12)", // Better divider visibility
            },
        },
    },
    typography: {
        fontFamily: "'Inter Variable', 'Roboto', sans-serif",
        // Typography optimized for clarity in a technical application
        h1: {
            fontWeight: 700,
            fontSize: "2.5rem",
            lineHeight: 1.2,
        },
        h2: {
            fontWeight: 600,
            fontSize: "2rem",
            lineHeight: 1.3,
        },
        h3: {
            fontWeight: 600,
            fontSize: "1.5rem",
            lineHeight: 1.4,
        },
        h4: {
            fontWeight: 500,
            fontSize: "1.25rem",
            lineHeight: 1.4,
        },
        // For labels and standard text
        body1: {
            fontSize: "1rem",
            lineHeight: 1.5,
        },
        // For dimensions and measurements - needs to be very clear
        subtitle1: {
            fontWeight: 600,
            fontSize: "1.125rem",
            lineHeight: 1.4,
            fontFamily: "'JetBrains Mono Variable', monospace", // Monospace for number alignment
        },
        // For smaller details and annotations
        caption: {
            fontSize: "0.875rem",
            lineHeight: 1.4,
            fontFamily: "'JetBrains Mono Variable', monospace",
        },
    },
});
